import {AbstractApi} from '@/service/api/AbstractApi'
import {I18n} from 'vue-i18n'
import {AuthenticationService} from '@/service/authentication/AuthenticationService'
import {SiteBean, SiteDeliveryAddressBean} from '@/service/model/SiteBean'
import {
  SiteManagerAddressBeanV2,
  SiteManagerSiteBeanV2,
  siteManagerSiteBeanV2ArraySchema
} from '@/service/model/sitemanager/SiteManagerSiteBeanV2'

function mapAddressBeanV2ToSiteAddressBean(address: SiteManagerAddressBeanV2): SiteDeliveryAddressBean {
  return {
    address_1: address.address1 ?? null,
    address_2: address.address2 ?? null,
    postal_code: address.postalCode ?? null,
    city: address.city ?? null,
    location: address.location ?? null
  }
}

function mapAddressToSiteAddressBean(address: any): SiteDeliveryAddressBean {
  return {
    address_1: address?.address1 ?? address?.address_1 ?? null,
    address_2: address?.address2 ?? address?.address_2 ?? null,
    postal_code: address?.postalCode ?? address?.postal_code ?? null,
    city: address?.city ?? null,
    location: address?.location ?? null
  }
}

function mapSiteBeanV2ToSiteBean(site: SiteManagerSiteBeanV2): SiteBean {
  const addresses = (site.addresses ?? []).map(mapAddressBeanV2ToSiteAddressBean)
  return {
    id: site.id,
    name: site.name,
    reference: site.reference ?? null,
    address_1: site.address1 ?? null,
    address_2: site.address2 ?? null,
    postal_code: site.postalCode ?? null,
    city: site.city ?? null,
    location: site.location ?? null,
    inExclusivityZone: site.inExclusivityZone ?? false,
    addresses,
    delivery_addresses: addresses,
    deliveryAddresses: addresses
  }
}

function mapAnySiteToSiteBean(site: any): SiteBean | null {
  const id = site?.id ?? site?.siteId ?? site?.site_id
  const name = site?.name ?? site?.siteName ?? site?.site_name ?? site?.label ?? site?.reference ?? site?.siteReference ?? site?.site_reference
  const reference = site?.reference ?? site?.siteReference ?? site?.site_reference ?? null
  if (id == null || name == null) {
    return null
  }
  const rawAddresses = Array.isArray(site?.addresses) ? site.addresses : []
  const addresses = rawAddresses.map(mapAddressToSiteAddressBean)
  return {
    id: String(id),
    name: String(name),
    reference: reference == null ? null : String(reference),
    address_1: site?.address1 ?? site?.address_1 ?? null,
    address_2: site?.address2 ?? site?.address_2 ?? null,
    postal_code: site?.postalCode ?? site?.postal_code ?? null,
    city: site?.city ?? null,
    location: site?.location ?? null,
    inExclusivityZone: site?.inExclusivityZone ?? site?.in_exclusivity_zone ?? false,
    addresses,
    delivery_addresses: addresses,
    deliveryAddresses: addresses
  }
}

function extractRawSites(content: any): any[] {
  if (Array.isArray(content)) {
    return content
  }
  if (Array.isArray(content?.sites)) {
    return content.sites
  }
  if (Array.isArray(content?.items)) {
    return content.items
  }
  if (Array.isArray(content?.content)) {
    return content.content
  }
  if (Array.isArray(content?.data)) {
    return content.data
  }
  return []
}

function normalizeSites(content: any): SiteBean[] {
  return extractRawSites(content)
    .map(mapAnySiteToSiteBean)
    .filter((it): it is SiteBean => it != null)
}

export class SiteManagerSiteApi extends AbstractApi {
  
  constructor(i18n: I18n, authenticationService: AuthenticationService) {
    super(i18n, authenticationService)
  }
  
  async fetchSites(): Promise<Array<SiteBean>> {
    try {
      const sites = await this.get<Array<SiteManagerSiteBeanV2>>({
        path: '/api/mp/v2/site-customer/sites',
        authentication: 'required',
        schema: siteManagerSiteBeanV2ArraySchema
      })
      const mappedSites = sites.map(mapSiteBeanV2ToSiteBean)
      if (mappedSites.length > 0) {
        return mappedSites
      }
    } catch {
      // Fall through to raw parsing and v1 fallback
    }

    try {
      const rawV2Content = await this.get<any>({
        path: '/api/mp/v2/site-customer/sites',
        authentication: 'required'
      })
      const normalizedV2Sites = normalizeSites(rawV2Content)
      if (normalizedV2Sites.length > 0) {
        return normalizedV2Sites
      }
    } catch {
      // Fall through to v1 endpoint fallback
    }

    const rawV1Content = await this.get<any>({
      path: '/api/mp/v1/site-manager/sites',
      authentication: 'required'
    })
    return normalizeSites(rawV1Content)
  }
}
